import React, { useState } from 'react';
import Icon from 'components/AppIcon';
import supabase from '../../../lib/supabase';

const InvestorRequestForm = ({ onAccessGranted }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    investmentInterest: '',
    message: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData?.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.organization?.trim()) {
      newErrors.organization = 'Organization is required';
    }

    if (!formData?.investmentInterest?.trim()) {
      newErrors.investmentInterest = 'Investment interest is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setSubmitError('');

    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const { data, error } = await supabase?.functions?.invoke('send-investor-email', {
        body: {
          name: formData?.name,
          email: formData?.email,
          organization: formData?.organization,
          investmentInterest: formData?.investmentInterest,
          message: formData?.message
        }
      });

      if (error) {
        // FunctionsHttpError contains the actual error from the edge function
        const errMsg = error?.message || '';
        console.error('Supabase functions invoke error:', error);
        // If it's a non-critical error (domain config), still show success
        if (errMsg?.includes('domain') || errMsg?.includes('sender') || errMsg?.includes('not configured')) {
          setSubmitted(true);
          return;
        }
        throw new Error(errMsg || 'Failed to send email');
      }

      // data.error means the edge function returned an error in the body
      if (data?.error) {
        console.error('Edge function returned error:', data?.error);
        throw new Error(data.error);
      }

      // success (including graceful fallbacks from edge function)
      setSubmitted(true);
    } catch (err) {
      console.error('Investor email error:', err);
      const msg = err?.message || '';
      if (msg?.includes('not configured') || msg?.includes('domain') || msg?.includes('sender')) {
        // Configuration issue — still show success, lead is logged server-side
        setSubmitted(true);
      } else {
        setSubmitError(
          msg
            ? `Error: ${msg}. Please try again or contact support@lawdesk.co.ke`
            : 'There was an issue sending your access link. Please try again or contact support@lawdesk.co.ke'
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Success confirmation screen ─────────────────────────────────────────────
  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-secondary-50 flex items-center justify-center px-4 py-12">
        <div className="max-w-lg w-full">
          <div className="bg-surface rounded-2xl shadow-lg p-10 text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-success-100 rounded-full mb-6">
              <Icon name="MailCheck" size={40} className="text-success-600" />
            </div>
            <h1 className="text-2xl font-bold text-text-primary mb-3">
              Check Your Email!
            </h1>
            <p className="text-text-secondary mb-6 leading-relaxed">
              We've sent your exclusive pitch deck access link to{' '}
              <span className="font-semibold text-primary-600">{formData?.email}</span>.
              Please check your inbox (and spam folder) for an email from LawDesk KE.
            </p>

            <div className="bg-primary-50 border border-primary-200 rounded-xl p-5 mb-6 text-left">
              <p className="text-sm font-semibold text-primary-700 mb-3 flex items-center">
                <Icon name="Info" size={16} className="mr-2" />
                What's in the email?
              </p>
              <ul className="space-y-2 text-sm text-text-secondary">
                <li className="flex items-start">
                  <Icon name="Check" size={14} className="text-success-500 mr-2 mt-0.5 flex-shrink-0" />
                  Your personalized pitch deck access link
                </li>
                <li className="flex items-start">
                  <Icon name="Check" size={14} className="text-success-500 mr-2 mt-0.5 flex-shrink-0" />
                  A summary of your registered investment details
                </li>
                <li className="flex items-start">
                  <Icon name="Check" size={14} className="text-success-500 mr-2 mt-0.5 flex-shrink-0" />
                  Contact information for follow-up discussions
                </li>
              </ul>
            </div>

            <p className="text-sm text-text-secondary mb-6">
              Questions? Reach us at{' '}
              <a href="mailto:support@lawdesk.co.ke" className="text-primary-600 font-medium hover:underline">
                support@lawdesk.co.ke
              </a>{' '}or call <span className="font-medium">0727265443</span>.
            </p>

            <button
              onClick={() => onAccessGranted(formData)}
              className="w-full bg-primary-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-primary-700 transition-smooth flex items-center justify-center"
            >
              <Icon name="ExternalLink" size={18} className="mr-2" />
              View Pitch Deck Now
            </button>
            <p className="text-xs text-text-secondary mt-3">
              You can also access it anytime via the link in your email.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── Request form ────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-secondary-50 flex items-center justify-center px-4 py-12">
      <div className="max-w-2xl w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full mb-4">
            <Icon name="Lock" size={32} className="text-primary-600" />
          </div>
          <h1 className="text-3xl font-bold text-text-primary mb-2">
            Investor Access Request
          </h1>
          <p className="text-text-secondary">
            Please provide your details to receive the LawDesk KE pitch deck access link via email
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-surface rounded-lg shadow-lg p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name Field */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-text-primary mb-2">
                Full Name <span className="text-error-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData?.name}
                onChange={handleChange}
                className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition-smooth ${
                  errors?.name
                    ? 'border-error-500 focus:ring-error-500' : 'border-border focus:ring-primary-500'
                }`}
                placeholder="John Doe"
              />
              {errors?.name && (
                <p className="mt-1 text-sm text-error-500 flex items-center">
                  <Icon name="AlertCircle" size={14} className="mr-1" />
                  {errors?.name}
                </p>
              )}
            </div>

            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-text-primary mb-2">
                Email Address <span className="text-error-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData?.email}
                onChange={handleChange}
                className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition-smooth ${
                  errors?.email
                    ? 'border-error-500 focus:ring-error-500' : 'border-border focus:ring-primary-500'
                }`}
                placeholder="john@example.com"
              />
              {errors?.email && (
                <p className="mt-1 text-sm text-error-500 flex items-center">
                  <Icon name="AlertCircle" size={14} className="mr-1" />
                  {errors?.email}
                </p>
              )}
            </div>

            {/* Organization Field */}
            <div>
              <label htmlFor="organization" className="block text-sm font-medium text-text-primary mb-2">
                Organization <span className="text-error-500">*</span>
              </label>
              <input
                type="text"
                id="organization"
                name="organization"
                value={formData?.organization}
                onChange={handleChange}
                className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition-smooth ${
                  errors?.organization
                    ? 'border-error-500 focus:ring-error-500' : 'border-border focus:ring-primary-500'
                }`}
                placeholder="Investment Firm Name"
              />
              {errors?.organization && (
                <p className="mt-1 text-sm text-error-500 flex items-center">
                  <Icon name="AlertCircle" size={14} className="mr-1" />
                  {errors?.organization}
                </p>
              )}
            </div>

            {/* Investment Interest Field */}
            <div>
              <label htmlFor="investmentInterest" className="block text-sm font-medium text-text-primary mb-2">
                Investment Interest <span className="text-error-500">*</span>
              </label>
              <select
                id="investmentInterest"
                name="investmentInterest"
                value={formData?.investmentInterest}
                onChange={handleChange}
                className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition-smooth ${
                  errors?.investmentInterest
                    ? 'border-error-500 focus:ring-error-500' : 'border-border focus:ring-primary-500'
                }`}
              >
                <option value="">Select investment interest</option>
                <option value="seed">Seed Round</option>
                <option value="series-a">Series A</option>
                <option value="series-b">Series B+</option>
                <option value="strategic">Strategic Partnership</option>
                <option value="angel">Angel Investment</option>
                <option value="venture">Venture Capital</option>
                <option value="other">Other</option>
              </select>
              {errors?.investmentInterest && (
                <p className="mt-1 text-sm text-error-500 flex items-center">
                  <Icon name="AlertCircle" size={14} className="mr-1" />
                  {errors?.investmentInterest}
                </p>
              )}
            </div>

            {/* Message Field (Optional) */}
            <div>
              <label htmlFor="message" className="block text-sm font-medium text-text-primary mb-2">
                Additional Message (Optional)
              </label>
              <textarea
                id="message"
                name="message"
                value={formData?.message}
                onChange={handleChange}
                rows={4}
                className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 transition-smooth resize-none"
                placeholder="Tell us more about your investment interests..."
              />
            </div>

            {/* Error message */}
            {submitError && (
              <div className="p-4 bg-error-50 border border-error-200 rounded-lg flex items-start">
                <Icon name="AlertCircle" size={18} className="text-error-500 mr-2 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-error-700">{submitError}</p>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-primary-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-smooth disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {isSubmitting ? (
                <>
                  <Icon name="Loader" size={20} className="mr-2 animate-spin" />
                  Sending Access Link...
                </>
              ) : (
                <>
                  <Icon name="Send" size={20} className="mr-2" />
                  Send Me the Pitch Deck Link
                </>
              )}
            </button>
          </form>

          {/* Security Notice */}
          <div className="mt-6 p-4 bg-primary-50 rounded-lg border border-primary-200">
            <div className="flex items-start">
              <Icon name="Shield" size={20} className="text-primary-600 mr-2 mt-0.5" />
              <div className="text-sm text-text-secondary">
                <p className="font-medium text-text-primary mb-1">Privacy & Security</p>
                <p>Your information will be kept confidential and used solely for investment discussions. We respect your privacy and will not share your details with third parties.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvestorRequestForm;